package br.com.htms.inspeccaocampo.shared.ui.components

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.gestures.rememberTransformableState
import androidx.compose.foundation.gestures.transformable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.FilledTonalIconButton
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.input.pointer.PointerEventType
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import br.com.htms.inspeccaocampo.platform.PlatformServices
import br.com.htms.inspeccaocampo.platform.decodeToImageBitmap

private const val MIN_SCALE = 1f
private const val MAX_SCALE = 6f
private const val DOUBLE_TAP_SCALE = 2.5f
private const val ZOOM_STEP = 0.5f

@Composable
fun ManualDialog(onDismiss: () -> Unit) {
    var bitmap by remember { mutableStateOf<ImageBitmap?>(null) }
    var erro by remember { mutableStateOf<String?>(null) }
    val scaleState = remember { mutableStateOf(1f) }
    val offsetState = remember { mutableStateOf(Offset.Zero) }

    LaunchedEffect(Unit) {
        erro = null
        bitmap = null
        val bytes = listOf("manual.jpeg", "manual.jpg", "manual_procedimento.jpg")
            .firstNotNullOfOrNull { name ->
                PlatformServices.loadBundledAsset(name)?.takeIf { it.isNotEmpty() }
            }
        if (bytes == null) {
            erro = "Não foi possível carregar o manual."
        } else {
            bitmap = decodeToImageBitmap(bytes)
            if (bitmap == null) erro = "Formato de imagem não suportado."
        }
    }

    fun aplicarZoom(novo: Float) {
        scaleState.value = novo.coerceIn(MIN_SCALE, MAX_SCALE)
        if (scaleState.value <= 1.05f) offsetState.value = Offset.Zero
    }

    val transformState = rememberTransformableState { zoomChange, panChange, _ ->
        aplicarZoom(scaleState.value * zoomChange)
        if (scaleState.value > 1f) offsetState.value += panChange
    }

    val scale = scaleState.value
    val offset = offsetState.value

    Dialog(
        onDismissRequest = onDismiss,
        properties = DialogProperties(
            usePlatformDefaultWidth = false,
            dismissOnBackPress = true,
            dismissOnClickOutside = true
        )
    ) {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .background(Color.Black.copy(alpha = 0.96f))
        ) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(top = 64.dp, bottom = 120.dp)
                    .pointerInput(Unit) {
                        detectTapGestures(
                            onDoubleTap = {
                                if (scaleState.value > 1.05f) aplicarZoom(1f)
                                else aplicarZoom(DOUBLE_TAP_SCALE)
                            }
                        )
                    }
                    .pointerInput(Unit) {
                        awaitPointerEventScope {
                            while (true) {
                                val event = awaitPointerEvent()
                                if (event.type != PointerEventType.Scroll) continue
                                val dy = event.changes.firstOrNull()?.scrollDelta?.y ?: continue
                                if (dy == 0f) continue
                                val factor = if (dy < 0f) 1.12f else 0.89f
                                aplicarZoom(scaleState.value * factor)
                                event.changes.forEach { it.consume() }
                            }
                        }
                    },
                contentAlignment = Alignment.Center
            ) {
                when {
                    bitmap != null -> {
                        Image(
                            bitmap = bitmap!!,
                            contentDescription = "Manual do procedimento",
                            modifier = Modifier
                                .fillMaxSize()
                                .graphicsLayer {
                                    scaleX = scale
                                    scaleY = scale
                                    translationX = offset.x
                                    translationY = offset.y
                                }
                                .transformable(state = transformState),
                            contentScale = ContentScale.Fit
                        )
                    }
                    erro != null -> {
                        Text(
                            text = erro!!,
                            color = Color.White,
                            modifier = Modifier.padding(24.dp)
                        )
                    }
                    else -> CircularProgressIndicator(color = Color.White)
                }
            }

            Surface(
                modifier = Modifier
                    .fillMaxWidth()
                    .align(Alignment.TopCenter),
                color = Color.Black.copy(alpha = 0.72f)
            ) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 4.dp, vertical = 2.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(
                        text = "Manual — Procedimento",
                        color = Color.White,
                        fontWeight = FontWeight.SemiBold,
                        modifier = Modifier
                            .weight(1f)
                            .padding(start = 12.dp)
                    )
                    IconButton(onClick = onDismiss) {
                        Text("✕", color = Color.White, fontSize = 18.sp)
                    }
                }
            }

            Surface(
                modifier = Modifier
                    .fillMaxWidth()
                    .align(Alignment.BottomCenter),
                color = Color.Black.copy(alpha = 0.72f)
            ) {
                Column(
                    modifier = Modifier.padding(horizontal = 16.dp, vertical = 12.dp),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.spacedBy(10.dp)
                ) {
                    Text(
                        text = "Pinça, toque duplo ou botões para zoom • Arraste para mover",
                        color = Color.White.copy(alpha = 0.88f),
                        style = MaterialTheme.typography.bodySmall
                    )
                    Row(
                        horizontalArrangement = Arrangement.spacedBy(10.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        FilledTonalIconButton(onClick = { aplicarZoom(scale - ZOOM_STEP) }) {
                            Text(text = "−", fontSize = 22.sp)
                        }
                        Text(
                            text = "${(scale * 100).toInt()}%",
                            color = Color.White,
                            style = MaterialTheme.typography.bodyMedium,
                            modifier = Modifier.widthIn(min = 52.dp)
                        )
                        FilledTonalIconButton(onClick = { aplicarZoom(scale + ZOOM_STEP) }) {
                            Text(text = "+", fontSize = 20.sp, fontWeight = FontWeight.Bold)
                        }
                        FilledTonalIconButton(onClick = { aplicarZoom(1f) }) {
                            Text(text = "↺", fontSize = 18.sp)
                        }
                    }
                }
            }
        }
    }
}
